import { api } from "@/api";
import { H3 } from "@/components/Heading";
import { MailToLink } from "@/components/Link";
import { translations } from "@/constants/translations";
import { renderInnerHtml } from "@/helpers/dom";
import { useStore } from "@/store";
import { Box, Button, HStack, Input, Text } from "@chakra-ui/react";
import { observer } from "mobx-react";
import React from "react";
import { useState } from "react";
import toast from "react-hot-toast";
import { useApiMutate } from "@/api/hooks/useApiMutate";
import { getApiErrorMessage } from "@/api/getApiErrorMessage";
import { useCaptchaToken } from "@/components/Captcha/useCaptchaToken";
import { telemetry } from "@/telemetry";

interface Props {
  onValidated: (email: string) => unknown;
}

export const ValidateEmailForm = observer(({ onValidated }: Props) => {
  const { activeLanguage } = useStore();
  const [email, setEmail] = useState("");
  const language = translations.pages["emergent-account"][activeLanguage];

  const getCaptchaToken = useCaptchaToken("verify_email");

  const apiValidateEmail = useApiMutate({
    mutationFn: async (data: { email: string; language: string }) => {
      const captchaToken = await getCaptchaToken();
      return api.verifyEmail({ ...data, captchaToken });
    },
    onSuccess: () => {
      telemetry.trackEvent("emergent_email_submitted", { result: "ok" });
      onValidated(email);
    },
    onError: (data) => {
      telemetry.trackEvent("emergent_email_submitted", {
        result: "error",
        status: data.response?.status,
      });
      toast.error(getApiErrorMessage(data));
    },
  });

  const handleSubmit = () => {
    apiValidateEmail.mutate({
      email,
      language: activeLanguage,
    });
  };

  return (
    <>
      <H3>{renderInnerHtml(language.enter_email)}</H3>
      <HStack
        spacing="6"
        flexDirection={{ base: "column", md: "row" }}
      >
        <Text
          minW="120px"
          fontWeight="bold"
        >
          Email address
        </Text>
        <Input
          autoFocus
          maxWidth="312px"
          placeholder="Enter your email address"
          onChange={(e) => setEmail(e.target.value)}
        />
        <Button
          onClick={handleSubmit}
          isLoading={apiValidateEmail.isPending}
          width="260px"
        >
          Continue to order
        </Button>
      </HStack>

      <Text>
        Forgot which email address is registered with your account? Please
        contact Emergent customer service at{" "}
        <Box
          display="inline"
          color="primary-light"
        >
          1-844-898-0657
        </Box>{" "}
        or{" "}
        <MailToLink
          color="primary-light"
          href={language.customer_email}
          subject="Request from orderNARCAN.ca site — Please help with direct account"
        >
          {language.customer_email}
        </MailToLink>
        .
      </Text>
    </>
  );
});
